import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, FileDown } from "lucide-react";

const hm = (iso) => (iso ? iso.slice(11, 16) : "—");

export default function WorkerMonth({ userId, refreshKey }) {
  const { t, tn, lang } = useI18n();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [showDays, setShowDays] = useState(false);
  const [showWorks, setShowWorks] = useState(false);

  useEffect(() => {
    const q = `/worktime/my-stats?month=${month}` + (userId ? `&user_id=${userId}` : "");
    api.get(q).then((r) => setData(r.data)).catch(() => setData(null));
  }, [month, userId, refreshKey]);

  const pdf = async () => {
    try {
      const emp = userId ? `&employee_ids=${userId}` : "";
      const today = new Date().toISOString().slice(0, 10);
      const to = data.date_to > today ? today : data.date_to;
      const f = new Date(to); f.setDate(f.getDate() - 6); const from = f.toISOString().slice(0, 10) < data.date_from ? data.date_from : f.toISOString().slice(0, 10);
      const { data: blob } = await api.get(`/pdf/report?date_from=${from}&date_to=${to}&lang=${lang}${emp}`, { responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error(t("error")); }
  };

  const Kpi = ({ label, value, testid }) => (
    <div className="bg-[var(--bg)] rounded-xl p-3">
      <div className="text-[11px] uppercase text-[var(--text-3)] font-semibold">{label}</div>
      <div className="font-mono-num text-xl font-bold mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );

  return (
    <div className="tactical-card p-4 space-y-3" data-testid="worker-month">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-[var(--text-3)] font-semibold flex-1 min-w-[120px]">{t("my_month")}</div>
        <input data-testid="month-picker" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 border border-[var(--border)] rounded-lg px-2 text-sm bg-[var(--bg)] max-w-full" />
      </div>
      {!data ? <div className="text-sm text-[var(--text-3)] text-center py-2">{t("no_data")}</div> : (<>
        <div className="grid grid-cols-2 gap-2">
          <Kpi label={t("work_days")} value={data.work_days} testid="stat-days" />
          <Kpi label={t("hours")} value={`${data.total_hours}h`} testid="stat-hours" />
          <Kpi label={t("avg_per_day")} value={`${data.avg_hours}h`} testid="stat-avg" />
          <Kpi label={t("units_done")} value={data.units_total} testid="stat-units" />
          <Kpi label={t("productive_time")} value={`${data.productive_minutes}′`} testid="stat-prod" />
          <Kpi label={t("non_productive_time")} value={`${data.non_productive_minutes}′`} testid="stat-nonprod" />
          <Kpi label={t("unallocated")} value={`${data.unallocated_minutes}′`} testid="stat-unalloc" />
          {data.efficiency !== undefined && <Kpi label={t("efficiency")} value={data.efficiency != null ? `${data.efficiency}%` : "—"} testid="stat-eff" />}
          {data.cost !== undefined && <Kpi label={t("cost")} value={`€${data.cost}`} testid="stat-cost" />}
        </div>

        <button data-testid="toggle-days" onClick={() => setShowDays((s) => !s)} className="w-full flex items-center justify-between text-sm font-semibold py-1">
          <span>{t("by_day")} ({data.days.length})</span>{showDays ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {showDays && (
          <div className="divide-y text-sm" data-testid="days-list">
            {data.days.length === 0 && <div className="text-[var(--text-3)] py-2 text-center">{t("no_data")}</div>}
            {data.days.map((d) => (
              <div key={d.id} className="flex items-center gap-2 py-1.5 flex-wrap">
                <span className="font-semibold w-24 shrink-0">{d.date}</span>
                <span className="font-mono-num flex-1 min-w-0 text-[var(--text-3)] whitespace-nowrap">{hm(d.start_time)}–{hm(d.end_time)} · {d.break_minutes || 0}′</span>
                <span className="font-mono-num font-bold shrink-0">{d.hours}h</span>
              </div>
            ))}
          </div>
        )}

        <button data-testid="toggle-works" onClick={() => setShowWorks((s) => !s)} className="w-full flex items-center justify-between text-sm font-semibold py-1">
          <span>{t("works_done")} ({data.works.length})</span>{showWorks ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {showWorks && (
          <div className="divide-y text-sm" data-testid="works-list">
            {data.works.map((w) => (
              <div key={w.work_type_id} className="py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  {w.item_number && <span className="text-xs text-[var(--text-3)] font-mono-num shrink-0">{w.item_number}</span>}
                  <span className="flex-1 min-w-0 break-words">{tn(w.work_name)}</span>
                  <span className="font-mono-num font-semibold shrink-0 whitespace-nowrap">{w.quantity} {tn(w.unit_name)}</span>
                </div>
                <div className="text-xs text-[var(--text-3)] font-mono-num">
                  {t("actual_min_short")}: {w.actual_minutes}
                  {w.norm_minutes !== undefined && ` · ${t("norm_min_short")}: ${w.norm_minutes ? Math.round(w.norm_minutes) : "—"}`}
                  {w.activity_type === "NON_PRODUCTIVE"
                    ? ` · ${t("NON_PRODUCTIVE")}${w.norm_usage_pct != null ? ` · ${w.norm_usage_pct}% ${t("norm_usage")}` : ""}`
                    : (w.efficiency !== undefined ? ` · ${t("efficiency")}: ${w.efficiency != null ? w.efficiency + "%" : "—"}` : "")}
                </div>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" onClick={pdf} className="w-full gap-2 h-10" data-testid="month-pdf-btn"><FileDown size={15} /> PDF · 7 {t("days")}</Button>
      </>)}
    </div>
  );
}
